import { computed, ref } from 'vue'
import { brands, getBrandByDomain, type BrandConfig, type BrandKey } from '@/config/brands'
import { useCurrentHost } from '@/composables/useCurrentHost'

// Reactive brand reference (allows override for testing)
const brandOverride = ref<BrandKey | null>(null)

/**
 * Composable for brand detection and configuration
 * Now unified under Zaveato brand
 */
export function useBranding() {
  /**
   * Current brand configuration - always Zaveato
   */
  const brand = computed<BrandConfig>(() => {
    // Check for override first (useful for testing)
    if (brandOverride.value) {
      return brands[brandOverride.value]
    }

    // Check for environment variable override
    const envOverride = import.meta.env.VITE_BRAND_OVERRIDE as BrandKey | undefined
    if (envOverride && brands[envOverride]) {
      return brands[envOverride]
    }

    // Detect from domain (always returns Zaveato now). Hostname sourced
    // from the single-source composable — no direct window.location reads
    // here (SRP: `useCurrentHost` owns hostname parsing).
    if (typeof window !== 'undefined') {
      const { hostname } = useCurrentHost()
      return getBrandByDomain(hostname.value, window.location.port)
    }

    // Default to Zaveato
    return brands.zaveato
  })

  /**
   * Is current brand Zaveato (unified platform) - always true
   */
  const isZaveato = computed(() => true)

  /**
   * Does current brand support service appointments - always true for Zaveato
   */
  const supportsServices = computed(() => brand.value.features.appointments)

  /**
   * Does current brand support shop/e-commerce - always true for Zaveato
   */
  const supportsShop = computed(() => brand.value.features.shop)

  /**
   * Set brand override (for testing)
   */
  function setBrandOverride(key: BrandKey | null) {
    brandOverride.value = key
  }

  /**
   * Get Tailwind gradient classes for the brand
   */
  const gradientClasses = computed(() => {
    const { from, to } = brand.value.colors.gradient
    return `from-${from} to-${to}`
  })

  /**
   * Get CSS gradient style for inline use
   */
  const gradientStyle = computed(() => {
    const { fromHex, toHex } = brand.value.colors.gradient
    return `linear-gradient(135deg, ${fromHex} 0%, ${toHex} 100%)`
  })

  /**
   * Get primary button classes
   * @deprecated Use brand.value.classes.buttonPrimary instead
   */
  const primaryButtonClasses = computed(() => {
    return brand.value.classes.buttonPrimary
  })

  /**
   * Pre-built themed classes from brand config
   * Use these for consistent branding across the app
   */
  const classes = computed(() => brand.value.classes)

  /**
   * Brand colors
   */
  const colors = computed(() => brand.value.colors)

  /**
   * Logo configuration including font family
   */
  const logo = computed(() => brand.value.logo)

  /**
   * Set document title based on brand
   */
  function setDocumentTitle(pageTitle?: string) {
    if (pageTitle) {
      document.title = `${pageTitle} | ${brand.value.name}`
    } else {
      document.title = brand.value.meta.title
    }
  }

  /**
   * Set brand data attribute on document
   */
  function setBrandDataAttribute() {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-brand', brand.value.key)
    }
  }

  return {
    brand,
    isZaveato,
    supportsServices,
    supportsShop,
    setBrandOverride,
    gradientClasses,
    gradientStyle,
    primaryButtonClasses,
    setDocumentTitle,
    setBrandDataAttribute,
    // New centralized theming
    classes,
    colors,
    logo
  }
}

/**
 * Set favicon dynamically based on brand
 */
function setFavicon(faviconPath?: string) {
  if (typeof document === 'undefined' || !faviconPath) return

  // Update SVG favicon
  const svgLink = document.querySelector('link[type="image/svg+xml"]') as HTMLLinkElement
  if (svgLink) {
    svgLink.href = faviconPath
  }

  // Update ICO favicon (fallback)
  const icoLink = document.querySelector('link[type="image/x-icon"]') as HTMLLinkElement
  if (icoLink) {
    icoLink.href = faviconPath
  }
}

/**
 * Initialize branding on app startup
 * Call this in App.vue onMounted
 */
export function initBranding() {
  const { brand, setBrandDataAttribute, setDocumentTitle } = useBranding()

  setBrandDataAttribute()
  setDocumentTitle()

  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]')
  if (metaDescription) {
    metaDescription.setAttribute('content', brand.value.meta.description)
  }

  // Update favicon for brand
  if (brand.value.logo.favicon) {
    setFavicon(brand.value.logo.favicon)
  }

  return brand
}
